import dom, { Fragment } from "../../dist/jsx-render/dom";
import JSXComponent from "../../dist/jsx-render/JSXComponent";
import { app } from "../Frame/App";
import { DropDownItemComponent } from "./DropDownItemComponent";
import { IDropdown } from "./Interfaces/IDropdown";

export type OnDropDownChange = (control: DropDownComponent) => void;

/**
 * Properties for DropDownComponent.
 */
export type DropDownComponentProps = {
    /** unique id for this control. */
    id: string;

    /** If set, a bootstrap label is created. */
    name?: string;

    /** This text will be displayed above the dropdown to show additional information */
    infoText?: string;

    /** True, if the control is disabled (readonly). */
    disabled?: boolean;

    /** Property used to get the item text. */
    displayProperty: string;

    /** Property used to get the item value. */
    valueProperty: string;

    /** Property used to get child items for tree display. */
    childrenProperty?: string;

    /** Expand all tree items on creation */
    expandAll?: boolean;

    /** 
     *  Initial items to show in the dropdown.
     *  In case of async load from server, use url property.
     */
    items?: Array<any>;

    /** A collection of items, that are added to the items this control gets from the server. */
    additionalItems?: Array<any>;


    /** Url to load data async from server. */
    url?: string;

    /** change event */
    onChange?: OnDropDownChange;

    /** This event is called after the Dropdown was created */
    onCreated?: (control: DropDownComponent) => void;

    /** Initially selected value. Setting this value will not trigger the onChange handler. */
    selectedValue?: any;
    selectFirstItemOnCreate?: boolean;
    triggerOnChangeForInitialValue?: boolean;

    /*will set the value directly on the display Text */
    selectDisplayText?: string;

    addEmptyItem?: boolean;
    emptyItemValue?: any;

    /** If true, items that have children can be selected. If false, a click on an item with children expands the children. 
     * A click on the expand icon always expands the children. */
    allowParentSelect?: boolean;

    /** disable automatic sort of items */
    disableSort?: boolean;

    /** get items via custom method */
    getItems?: () => JQueryPromise<any[]>;

    showLabel?: boolean;
    friendlyValueField?: string;

    model?: any;
    field?: string;
    modelValueType?: "string" | "number";
    undefinedValue?: any;

    /** If this is true, there will be a red asterisk next to the controls label. There is no further logic to this prop. */
    required?: boolean;
}

/**
 * Component for a autocomplete drop down.
 */
export class DropDownComponent extends JSXComponent implements IDropdown {
    props: DropDownComponentProps;

    private changeListener: Array<OnDropDownChange> = [];
    private selectedItem: any = null;
    private searchTimer: any;
    private dropDownCreated = false;
    private dropDownVisible = false;
    private ignoreFocus = false;
    private dropdownItems: Array<DropDownItemComponent> = [];
    private flatItems: DropDownItemComponent[] = [];

    constructor(props: DropDownComponentProps) {
        super(props);
    }

    /** Input control to display the name. */
    inputName: HTMLInputElement =
        <input type="text" className="form-control cc-name" />

    /** Input control to hold the value. */
    inputValue: HTMLInputElement =
        <input type="hidden" />

    /** Dropdown icon inside input name control. */
    iconDropdown: HTMLElement = (
        <i className="far fa-chevron-down cc-dropdown-icon" aria-haspopup="true" aria-expanded="false" data-reference="parent"></i>
    );

    dropdown: HTMLElement = (
        <div className="dropdown-menu dropdown-control">
            <div className="text-center">
                <div className="spinner-border mt-1 mb-1"></div>
            </div>
        </div>
    );

    /** The complete HTML element. */
    element: HTMLElement

    getElement = () => {
        if (this.props.name && this.props.name.length > 0) {
            this.element = <div className="form-group">

                {this.props.showLabel !== false
                    ? <label className="form-label">{this.props.name}{this.props.required ? <span className="red">*</span> : []}</label>
                    : []}

                {this.props.infoText
                    ? <div className="text-component-info info-text">
                        {this.props.infoText}
                    </div>
                    : []}

                <div className="dropdown-component dropdown" id={this.props.id}>
                    {this.inputName}
                    {this.inputValue}
                    {this.iconDropdown}
                    {this.dropdown}
                </div>
            </div>
        } else {
            this.element = <div className="dropdown-component dropdown" id={this.props.id}>
                {this.inputName}
                {this.inputValue}
                {this.iconDropdown}
                {this.dropdown}
            </div>
        }
    }

    addEventListener = (listener: OnDropDownChange) => {
        this.changeListener.push(listener);
    }

    /** Render the HTML control. */
    render(): HTMLElement {
        this.getElement();

        // set disabled state
        this.disable(this.props.disabled);

        // add listener from props
        if (this.props.onChange) {
            this.addOnChangeListener(this.props.onChange);
        }

        //if (this.props.model && this.props.field) {
        //    this.addOnChangeListener(this.onChange_BoundToModel);
        //}

        // create dropdown, if items available
        if (this.props.items) {
            this.createDropdown();
        }

        $(this.element).on('shown.bs.dropdown', this.onDropDownShown);
        $(this.element).on('hidden.bs.dropdown', this.onDropDownHidden);


        this.inputName.addEventListener("keydown", this.onKeyDown);
        this.inputName.addEventListener("focus", this.onFocus);

        this.iconDropdown.addEventListener("click", this.toggleDropdown);


        if (this.props.selectDisplayText && this.props.selectDisplayText.trim()) {
            this.setDisplayText(this.props.selectDisplayText, false);
        }

        else {
            if (this.props.selectedValue) {
                this.setValue(this.props.selectedValue, this.props.triggerOnChangeForInitialValue === true ? true : false);
            } else {
                if (this.props.onCreated) {
                    this.props.onCreated(this);
                }
            }
        }

        // if this control is bound to a model (meaning this.props.model and this.props.field have a value)
        // set the initial value here:
        if (this.props.model && this.props.field) {
            if (this.props.modelValueType === "number") {
                if (this.props.model[this.props.field] != undefined && this.props.model[this.props.field] != null) {
                    this.setValue(this.props.model[this.props.field].toString(), this.props.triggerOnChangeForInitialValue === true);
                }
            } else {
                this.setValue(this.props.model[this.props.field], this.props.triggerOnChangeForInitialValue === true);
            }
        }

        if (this.props.selectFirstItemOnCreate && this.flatItems.length) {
            this.setValue(this.flatItems[0].props.item[this.props.valueProperty], this.props.triggerOnChangeForInitialValue === true);
        }

        return this.element;
    }

    /**
     * Get the value of the selected item.
     */
    getValue(): string {
        return this.inputValue.value;
    }

    /**
     * Get the selected item model fdrom props.items. Returns null if no item is selected */
    public getSelectedItem(): any {
        return this.selectedItem;
    }

    /**
     * Select the item with the given value. Values will be parsed to string for comparison.
     */
    public setValue = (value: string, triggerOnChange: boolean = true): void => {
        this.createDropdown().done(() => {
            for (let item of this.flatItems) {

                if (item.props.item[this.props.valueProperty] != null
                    && item.props.item[this.props.valueProperty] != undefined
                    && item.props.item[this.props.valueProperty].toString() == value) {
                    this.select(item.props.item, triggerOnChange);
                    break;
                }
            }

            if (this.props.onCreated) {
                this.props.onCreated(this);
            }
        });
    }

    /**
     * Set value and display text of Select
     */
    public setDisplayText(displayText: string, triggerOnChange: boolean = true): void {
        this.createDropdown().done(() => {
            for (let item of this.flatItems) {
                if (item.props.item.text === displayText) {
                    this.select(item.props.item, triggerOnChange);
                    break;
                }
            }
        });
    }

    public removeSelection = () => {
        this.selectedItem = null;
        this.inputName.value = "";
        this.inputName.value = "";
    }

    isEmpty(): boolean {
        const value = this.inputValue.value;
        if (!value || value == "0") {
            return true;
        }
        return false;
    }

    /**
     * Add a new listener to the change event.
     * @param listener Listener of type OnDropDownChange.
     */
    public addOnChangeListener(listener: OnDropDownChange): void {
        this.changeListener.push(listener);
    }

    protected onDropDownShown = () => {
        this.dropDownVisible = true;
        if (!this.dropDownCreated) {
            this.createDropdown();
        }

        this.flatItems.forEach(i => {
            if (this.selectedItem == i.props.item) {

                i.highlight();
            } else {
                i.element.classList.remove("selected");
            }
        });
    }

    protected onDropDownHidden = () => {
        this.dropDownVisible = false;
    }

    protected onFocus = () => {
        if (!this.ignoreFocus) {
            this.showDropdown();
        }
        this.ignoreFocus = false;
    }

    /**
     * Fire change event.
     */
    protected onChange = (): void => {
        if (this.props.model && this.props.field) {
            this.onChange_BoundToModel(this);
        }

        for (let listener of this.changeListener) {
            listener(this);
        }
    }

    /**
     * Check if the control is disabled.
     * @returns True, if the control is disabled. */
    public isDiabled(): boolean {
        return this.props.disabled;
    }

    /**
     * Enable or disable the control.
     * @param value True, control is disabled.
     */
    public disable(value: boolean): void {
        this.props.disabled = value;
        if (value) {
            this.inputName.disabled = value;
            this.iconDropdown.classList.add("cc-disabled");
        }
        else {
            this.inputName.removeAttribute("disabled");
            this.iconDropdown.classList.remove("cc-disabled");
        }
    }

    /**
     * Show the dropdown element.
     */
    protected showDropdown = (): void => {
        this.createDropdown()
            .done(() => {
                $(this.dropdown).dropdown('show');
            })
            .fail((res: string) => {
                alert(res);
            });
    }

    /**
     * Toggle visibility of dropdown element.
     */
    protected toggleDropdown = (): void => {
        if (this.dropDownVisible) {
            this.hideDropdown();
        }
        else {
            this.showDropdown();
        }
    }

    /**
     * Check, if the dropdown element is visible.
     * @returns True, if visible.
     */
    protected isDropdownVisible(): boolean {
        return this.dropDownVisible;
    }

    /**
     * Hide the dropdown element.
     */
    protected hideDropdown(): void {
        $(this.dropdown).dropdown('hide');

        //this.dropdown.classList.remove("show");
        //window.removeEventListener("click", this.onDocMouseDown);
    }

    /**
     * Mouse down event, hide dropdown element if clicked outside of the control.
     * @param ev
     */
    protected onDocMouseDown = (ev: MouseEvent) => {
        let targetElement = ev.target as Node;
        while (targetElement) {
            if (targetElement == this.inputName.parentElement) {
                return;
            }
            targetElement = targetElement.parentNode;
        }
        this.hideDropdown();
    }

    /**
     * Keyboard event on input control.
     * @param ev
     */
    protected onKeyDown = (ev: KeyboardEvent) => {
        switch (ev.keyCode) {
            case 37: // left arrow
            case 39: // right arrow
            case 36: // home
            case 35: // end
                break;

            case 40: // down arrow
                if (this.dropDownVisible) {
                    for (let item of this.dropdownItems) {
                        if (!item.element.classList.contains("d-none")) {
                            item.element.focus();
                            break;
                        }
                    }
                }
                break;

            case 38: // up arrow
                break;

            case 13: //keyCode.ENTER:
                event.preventDefault();
                /*if (this.selectedItem) {
                    this.select(this.selectedItem);
                    this.hideDropdown();
                }*/
                break;

            case 9: //keyCode.TAB:
                // TODO if (self.menu.active) {
                /*self.menu.select(event);
                    }*/
                break;

            case 27: //keyCode.ESCAPE:
                this.hideDropdown();
                event.preventDefault();
                break;

            default:
                this.showDropdown();
                this.searchTimeout(ev);
                break;
        }
    }

    /**
     * Create the dropdown element.
     * @returns Promise object. 
     */
    public createDropdown(): JQueryPromise<string> {
        return $.Deferred((deferrer) => {
            if (this.dropDownCreated) {
                deferrer.resolve("");
            }
            else {
                if (this.props.items) {
                    this.createDropdownFromItems(this.props.items);
                    deferrer.resolve("");
                }
                else if (this.props.url) {
                    // add loading panel
                    this.dropdown.appendChild(
                        <div className="text-center">
                            <div className="spinner-border mt-1 mb-1"></div>
                        </div>
                    );
                    // create 
                    $.getJSON(app.resolveUrl(this.props.url))
                        .done((tree: any) => {
                            this.props.items = tree;
                            this.dropdown.innerHTML = "";

                            if (!this.props.disableSort) {
                                app.sortByRecursive(this.props.items, this.props.displayProperty, this.props.childrenProperty);
                            }

                            this.addEmptyItem();

                            for (let item of tree) {
                                this.appendItem(item);
                            }

                            if (this.props.additionalItems) {
                                for (let item of this.props.additionalItems) {
                                    this.appendItem(item);
                                }
                            }

                            this.dropDownCreated = true;

                            deferrer.resolve("");
                        })
                        .fail((res) => {
                            deferrer.reject(res.statusText);
                        });
                }
                else if (this.props.getItems) {
                    this.props.getItems()
                        .done((data: any[]) => {
                            this.createDropdownFromItems(data);
                            deferrer.resolve("");
                        })
                        .fail((res) => {
                            deferrer.reject(res.statusText);
                        });
                }
                else {
                    throw new Error("DropDownComponent: missing 'items' or 'url' in props!");
                }
            }
        }).promise();
    }

    private createDropdownFromItems(items: any[]) {
        // create dropdown from items
        this.dropdown.innerHTML = "";

        if (!this.props.disableSort) {
            app.sortByRecursive(items, this.props.displayProperty, this.props.childrenProperty);
        }

        this.addEmptyItem();

        for (let item of items) {
            let newItem = new DropDownItemComponent({
                item: item,
                displayProperty: this.props.displayProperty,
                valueProperty: this.props.valueProperty,
                childrenProperty: this.props.childrenProperty,
                onItemClick: this.onItemClick,
                expandAll: this.props.expandAll,
                flatItems: this.flatItems,
                allowParentSelect: this.props.allowParentSelect
            });
            this.dropdownItems.push(newItem);
            this.flatItems.push(newItem);
            this.dropdown.appendChild(newItem.render());
        }
        this.dropDownCreated = true;
    }

    public appendItem = (item: any) => {
        let newItem = new DropDownItemComponent({
            item: item,
            displayProperty: this.props.displayProperty,
            valueProperty: this.props.valueProperty,
            childrenProperty: this.props.childrenProperty,
            onItemClick: this.onItemClick,
            expandAll: this.props.expandAll,
            flatItems: this.flatItems,
            allowParentSelect: this.props.allowParentSelect
        });
        this.dropdownItems.push(newItem);
        this.flatItems.push(newItem);
        this.dropdown.appendChild(newItem.render());
    }

    private addEmptyItem = () => {
        if (this.props.addEmptyItem) {
            let emptyItem = new Object();
            emptyItem[this.props.displayProperty] = "";
            emptyItem[this.props.valueProperty] = this.props.emptyItemValue;
            emptyItem["isEmpty"] = true;
            this.props.items.splice(0, 0, emptyItem);
        }
    }

    /**
     * Hide dropdown element and select the given item.
     * @param item
     */
    protected onItemClick = (item: any): void => {
        setTimeout(() => {
            this.ignoreFocus = true;
            this.hideDropdown();
            this.select(item);
            this.inputName.focus();
        }, 0);
    }

    /**
     * Select the given item.
     * @param item
     */
    protected select(item: any, triggerOnChange: boolean = true) {
        this.inputName.value = item[this.props.displayProperty];
        this.inputValue.value = item[this.props.valueProperty];
        this.selectedItem = item;
        if (triggerOnChange) {
            this.onChange();
        }
    }

    public getSelectedText(): string {
        return this.inputName.value;
    }

    protected searchTimeout(ev: KeyboardEvent) {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
            this.search(this.inputName.value);
        }, 50);
    }

    protected search(value: string) {
        if (this.dropdown) {
            const matcher = new RegExp(this.escapeRegex(value), 'i');

            for (let item of this.dropdownItems) {
                if (!value || matcher.test(item.props.item[this.props.displayProperty])) {
                    item.element.classList.remove("d-none");
                    if (value) {
                        item.textElement.innerHTML = (item.props.item[this.props.displayProperty].replace(
                            new RegExp(
                                "(?![^&;]+;)(?!<[^<>]*)(" +
                                this.escapeRegex(value) +
                                ")(?![^<>]*>)(?![^&;]+;)", "gi"),
                            "<strong>$1</strong>"));
                    }
                    else {
                        item.textElement.innerHTML = item.props.item[this.props.displayProperty];
                    }
                }
                else {
                    item.element.classList.add("d-none");
                }
            }
        }
    }

    protected escapeRegex(value: string) {
        // return value.replace(/[\-\[\]{ }()*+?.,\\\^$|#\s]/g, "\\$&");
        return value.replace("[\\-\\[\\]{ }()*+?.,\\\\\\^$|#\s]", "\\$&");
    }

    private onChange_BoundToModel = (control: DropDownComponent) => {
        let val = this.getValue();

        if (val != null && val != undefined) {
            if (this.props.modelValueType === "number") {
                let valAsNumber = Number(val);
                if (!isNaN(valAsNumber)) {
                    this.props.model.setPropertyByPath(this.props.field, valAsNumber);
                }
            } else {
                this.props.model.setPropertyByPath(this.props.field, val);
            }
        } else {
            this.props.model[this.props.field] = null;
        }
    }

    public reset(items: Array<any>) {
        this.hideDropdown();
        this.props.items = items;
        this.dropDownCreated = false;
        this.dropdownItems = [];
        this.flatItems = [];
    }
}